import { useContext } from "react";
import { Card, Flex, Text } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useGetArenaQuery } from "~/graphql/mainHooks";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { ComplianceRulesContext } from "~/pages/Compliance/complianceRulesContext";
import { SPACING } from "~/styles/variables";
import { toGlobalId } from "~/utils/graphql-helpers";
import { generateGpMainUrl } from "~/utils/url";
import { CustomLabeledData } from "./CustomLabeledData";

interface Props {
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
}

export const CaseDetailsCard = ({ complianceCase }: Props) => {
  const rules = useContext(ComplianceRulesContext);
  const { data: arenaData } = useGetArenaQuery({
    context: withClientContext("staff"),
    variables: {
      id: toGlobalId("ArenaObjectType", complianceCase?.externalArena?.objectId || ""),
    },
  });
  if (!arenaData?.arena?.secondLevelDomain) {
    return null;
  }

  const gpMainUrl = generateGpMainUrl(arenaData.arena?.secondLevelDomain);

  const distributionBatchExternalObject = complianceCase?.externalObjects?.find(
    (obj) => obj.objectTypeEnum === "distribution_batch",
  );

  const paymentBatchExternalObject = complianceCase?.externalObjects?.find(
    (obj) => obj.objectTypeEnum === "payment_batch",
  );
  return (
    <Card>
      <Flex
        css={{ marginBottom: SPACING.LARGE }}
        alignContent="center"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text variant="secondary-md">CASE DETAILS</Text>
      </Flex>
      <Flex flexDirection="column" gap={8}>
        <CustomLabeledData
          label="Client"
          data={complianceCase?.externalArena?.name ?? ""}
          subdata={complianceCase?.externalArena?.objectId?.toString() ?? ""}
          url={`${gpMainUrl}/dashboard`}
        />
        <CustomLabeledData
          label="Entity"
          data={complianceCase?.externalEntity?.name ?? ""}
          subdata={complianceCase?.externalEntity?.objectId?.toString() ?? ""}
          url={`${gpMainUrl}/account/${complianceCase?.externalEntity?.objectId}`}
        />
        {distributionBatchExternalObject && (
          <CustomLabeledData
            label="Distribution batch"
            data={distributionBatchExternalObject.name ?? ""}
            subdata={distributionBatchExternalObject.objectId.toString() ?? ""}
            url={`${gpMainUrl}/distribution_batch/${distributionBatchExternalObject.objectId}`}
          />
        )}
        {paymentBatchExternalObject && distributionBatchExternalObject && (
          <CustomLabeledData
            label="Payment batch"
            data={paymentBatchExternalObject.name ?? ""}
            subdata={paymentBatchExternalObject.objectId ?? ""}
            url={`${gpMainUrl}/distribution_batch/${distributionBatchExternalObject.objectId}/automated_payment_batches/${paymentBatchExternalObject.objectId}`}
          />
        )}
        {rules.showIncomingPaymentDetails && complianceCase?.metaData && (
          <CustomLabeledData
            label="Modern Treasury incoming payment detail"
            data={"Modern Treasury incoming payment detail"}
            subdata=""
            url={complianceCase.metaData.incoming_payment_details_url as string}
          />
        )}
      </Flex>
    </Card>
  );
};
